import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { FeatureFlag, ISitesFeatureFlagsDictionary, normalizeFeatureFlags } from "./FeatureFlagsModels";
import type { RootState } from "../app/Store";
import { getMultiSiteFeatureFlagsAsync } from "./FeatureFlagsApi";
import { LoadingState } from "../app/LoadingState";
import { beginRequest, completeRequest, rejectRequest } from "../app/store/RequestState";

interface IFeatureFlagsState {
  sitesFeatureFlags: ISitesFeatureFlagsDictionary;
  isLoading: boolean;
  loadingState: LoadingState;
  activeRequestId?: string;
  loadingStateBeforeRequest?: LoadingState;
  error?: string;
}

const initialState: IFeatureFlagsState = {
  sitesFeatureFlags: {},
  isLoading: false,
  loadingState: LoadingState.None,
};

const featuresSlice = createSlice({
  name: "sitesFeatureFlags",
  initialState,
  reducers: {},
  extraReducers: (builder) =>
    builder
      .addCase(fetchFeatureFlags.pending, (state, action) => {
        beginRequest(state, action.meta.requestId);
        state.isLoading = true;
        state.loadingState = LoadingState.Pending;
        state.error = undefined;
      })
      .addCase(fetchFeatureFlags.fulfilled, (state, action) => {
        if (!completeRequest(state, action.meta.requestId)) {
          return;
        }
        state.sitesFeatureFlags = action.payload;
        state.isLoading = false;
        state.loadingState = LoadingState.Complete;
        state.error = undefined;
      })
      .addCase(fetchFeatureFlags.rejected, (state, action) => {
        if (rejectRequest(state, action)) {
          state.isLoading = false;
        }
      }),
});

export const fetchFeatureFlags = createAsyncThunk(
  "sitesFeatureFlags/fetch",
  async (siteIds: string[]) => {
    const enabledFeaturesPerSiteResponse = await getMultiSiteFeatureFlagsAsync(siteIds);
    return normalizeFeatureFlags(enabledFeaturesPerSiteResponse);
  }
);

export const featureFlagsReducer = featuresSlice.reducer;
export const selectSiteHasFeature = (state: RootState, siteId: string, feature: FeatureFlag): boolean =>
  state.featureFlags.sitesFeatureFlags[siteId]?.includes(feature) ?? false;
